/**
 * BrandDocumentHead — rewrites the browser's <title>, favicon and
 * social-share meta tags to match the resolved site brand.
 *
 * The base HTML shell (`public/index.html`) ships with SocialPulse
 * meta so scrapers that never execute JS still get a valid card.
 * This component runs on first paint and swaps in the branded copy
 * for the JS-aware experience — so socialcapital.cc shows a green
 * SocialCapital tab title, indianfamilytree.com shows heritage copy,
 * etc.
 *
 * Values live in one map so adding a new brand is a one-liner.
 */
import { useEffect } from "react";
import useSiteSlug from "@/hooks/useSiteSlug";

const BRAND_HEAD = {
  socialpulse: {
    title:       "SocialPulse | Global Community, Family Tree & Memorial Platform",
    description: "Discover communities, build your family tree, create memorials, explore local places, events, businesses, food, history, Civic Reports and more with SocialPulse.",
    themeColor:  "#7C3AED",
    ogSiteName:  "Social Pulse",
    ogUrl:       "https://socialpulse.com/",
  },
  socialcapital: {
    title:       "SocialCapital.cc — Recognize what truly matters",
    description: "SocialCapital.cc is where your positive contributions become a lifetime record — a Passport of Social Capital powered by SocialPulse®.",
    themeColor:  "#059669",
    ogSiteName:  "SocialCapital.cc",
    ogUrl:       "https://socialcapital.cc/",
  },
  indianfamilytree: {
    title:       "Indian Family Tree — Preserve every family's story",
    description: "Build your Indian family tree, honour ancestors, discover heritage stories and connect across generations on IndianFamilyTree.com.",
    themeColor:  "#B45309",
    ogSiteName:  "Indian Family Tree",
    ogUrl:       "https://indianfamilytree.com/",
  },
  foodpulse: {
    title:       "FoodPulse — Rescue food, cook better, share more",
    description: "Deals of the Day, urban harvest, food scouts and neighbourhood pantries — the community food network on FoodPulse.net.",
    themeColor:  "#EA580C",
    ogSiteName:  "Food Pulse",
    ogUrl:       "https://foodpulse.net/",
  },
  kerala: {
    title:       "Kerala Pulse — People, places and stories from God's own country",
    description: "Districts, tourism, community, food and stories from across Kerala on Kerala Pulse.",
    themeColor:  "#0E7C55",
    ogSiteName:  "Kerala Pulse",
    ogUrl:       "https://kerala.socialpulse.com/",
  },
  myfamilytreecare: {
    title:       "MyFamilyTree.care — Global heritage and memorial platform",
    description: "Preserve family memories, memorials and heritage stories on MyFamilyTree.care.",
    themeColor:  "#0E7C55",
    ogSiteName:  "MyFamilyTree.care",
    ogUrl:       "https://myfamilytree.care/",
  },
};

function upsertMeta(selector, attr, key, content) {
  if (typeof document === "undefined") return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export default function BrandDocumentHead() {
  const slug = useSiteSlug();
  useEffect(() => {
    const b = BRAND_HEAD[slug] || BRAND_HEAD.socialpulse;

    document.title = b.title;

    upsertMeta('meta[name="description"]',        "name",     "description",     b.description);
    upsertMeta('meta[name="theme-color"]',        "name",     "theme-color",     b.themeColor);
    upsertMeta('meta[property="og:site_name"]',   "property", "og:site_name",    b.ogSiteName);
    upsertMeta('meta[property="og:title"]',       "property", "og:title",        b.title);
    upsertMeta('meta[property="og:description"]', "property", "og:description",  b.description);
    upsertMeta('meta[property="og:url"]',         "property", "og:url",          b.ogUrl);
    upsertMeta('meta[name="twitter:title"]',      "name",     "twitter:title",   b.title);
    upsertMeta('meta[name="twitter:description"]',"name",     "twitter:description", b.description);
  }, [slug]);

  return null;
}
